import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, FileText, CheckCircle2, AlertCircle, Trash2, GraduationCap, Megaphone, Library } from "lucide-react";
import { toast } from "sonner";

const TAG_OPTIONS = [
  { value: "programme-material", label: "Programme material (general)" },
  { value: "deck", label: "Deck / slides" },
  { value: "bio", label: "Bio (PDF)" },
  { value: "transcript", label: "Transcript" },
  { value: "resource", label: "Resource library" },
];

const ATTACH_OPTIONS = [
  { value: "none", label: "Just upload (no attach)" },
  { value: "resource", label: "Add to Resource Library" },
  { value: "speaker:photo_url", label: "Attach to Speaker · photo" },
  { value: "speaker:bio_url", label: "Attach to Speaker · bio PDF" },
  { value: "speaker:deck_url", label: "Attach to Speaker · deck" },
  { value: "mentor:photo_url", label: "Attach to Mentor · photo" },
  { value: "mentor:bio_url", label: "Attach to Mentor · bio PDF" },
];

export default function ProgrammeUploader() {
  const inputRef = useRef(null);
  const [items, setItems] = useState([]); // {file, title, tag, attach, target_id, day_id, status, file_id, error}
  const [speakers, setSpeakers] = useState([]);
  const [mentors, setMentors] = useState([]);
  const [days, setDays] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const [s, m, d] = await Promise.all([
        api.get("/speakers").then((r) => r.data).catch(() => []),
        api.get("/mentors").then((r) => r.data).catch(() => []),
        api.get("/days").then((r) => r.data).catch(() => []),
      ]);
      setSpeakers(s); setMentors(m); setDays(d);
    })();
  }, []);

  const addFiles = (fileList) => {
    const next = Array.from(fileList).map((file) => ({
      key: `${file.name}-${file.size}-${Math.random().toString(36).slice(2)}`,
      file,
      title: file.name.replace(/\.[^.]+$/, ""),
      tag: "programme-material",
      attach: "none",
      target_id: "",
      day_id: "",
      description: "",
      status: "pending",
      file_id: null,
      url: null,
      error: null,
    }));
    setItems((prev) => [...prev, ...next]);
  };

  const onPick = (e) => e.target.files && addFiles(e.target.files);

  const onDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const onDragOver = (e) => e.preventDefault();

  const removeRow = (key) => setItems((prev) => prev.filter((r) => r.key !== key));
  const patch = (key, p) => setItems((prev) => prev.map((r) => r.key === key ? { ...r, ...p } : r));

  const processOne = async (row) => {
    patch(row.key, { status: "uploading", error: null });
    try {
      // 1. Upload
      const fd = new FormData();
      fd.append("file", row.file);
      const { data: uploaded } = await api.post(`/files/upload?tag=${encodeURIComponent(row.tag)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fileId = uploaded.id;
      const downloadUrl = `/api/files/${fileId}/download`;
      patch(row.key, { file_id: fileId, url: downloadUrl, status: "attaching" });

      // 2. Attach
      if (row.attach === "resource") {
        await api.post("/resources", {
          title: row.title || row.file.name,
          file_id: fileId,
          description: row.description || null,
          day_id: row.day_id || null,
          category: "programme-material",
        });
      } else if (row.attach.startsWith("speaker:") || row.attach.startsWith("mentor:")) {
        if (!row.target_id) {
          patch(row.key, { status: "error", error: "Select a target" });
          return;
        }
        const [target_type, field] = row.attach.split(":");
        await api.post("/admin/uploads/attach", {
          file_id: fileId, target_type, target_id: row.target_id, field,
        });
      }
      patch(row.key, { status: "done" });
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Upload failed";
      patch(row.key, { status: "error", error: typeof msg === "string" ? msg : "Upload failed" });
    }
  };

  const processAll = async () => {
    setBusy(true);
    try {
      for (const r of items) {
        if (r.status === "done" || r.status === "uploading") continue;
        await processOne(r);
      }
      toast.success("Batch complete");
    } finally { setBusy(false); }
  };

  const pendingCount = items.filter((r) => r.status === "pending" || r.status === "error").length;
  const doneCount = items.filter((r) => r.status === "done").length;

  return (
    <div className="space-y-8" data-testid="programme-uploader-page">
      <div>
        <div className="label-eyebrow flex items-center gap-2"><Upload size={12} /> Admin · bulk programme materials</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">Upload &amp; attach</h1>
        <p className="text-[#525252] mt-2 max-w-2xl text-sm">
          Drop one or many PDFs/decks/images. For each, choose where it goes — Resource Library, Speaker card, or Mentor card — and click <em>Upload all</em>. Files are stored in object storage; attachments wire the URL into the right record.
        </p>
      </div>

      {/* Drop zone */}
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        onClick={() => inputRef.current?.click()}
        className="border-4 border-dashed border-[#0A0A0A] bg-white p-12 text-center cursor-pointer hover:bg-[#FAFAFA]"
        data-testid="uploader-dropzone"
      >
        <Upload size={32} className="mx-auto text-[#2596be]" />
        <div className="font-display text-2xl font-black tracking-tighter mt-3">Drop files here</div>
        <div className="text-sm text-[#525252] mt-1">or click to browse — PDFs, slides, images, transcripts. Multiple files OK.</div>
        <input ref={inputRef} type="file" className="hidden" multiple onChange={onPick} data-testid="uploader-file-input" />
      </div>

      {items.length > 0 && (
        <div className="space-y-3" data-testid="uploader-queue">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="text-sm">
              <strong>{items.length}</strong> file{items.length === 1 ? "" : "s"} queued — <span className="text-[#2596be]">{doneCount} done</span>{pendingCount > 0 && <span>, {pendingCount} pending</span>}
            </div>
            <Button onClick={processAll} disabled={busy || pendingCount === 0} className="rounded-none bg-[#2596be] hover:bg-[#1e7da3]" data-testid="uploader-process-all">
              {busy ? "Uploading…" : `Upload all (${pendingCount})`}
            </Button>
          </div>

          {items.map((row) => (
            <div key={row.key} className="border border-[#E5E7EB] bg-white p-4 grid grid-cols-1 lg:grid-cols-12 gap-3 items-start" data-testid={`uploader-row-${row.key}`}>
              <div className="lg:col-span-3 flex items-center gap-2">
                <FileText size={18} className="text-[#525252] shrink-0" />
                <div className="text-xs">
                  <div className="font-semibold truncate" title={row.file.name}>{row.file.name}</div>
                  <div className="text-[#525252]">{Math.round(row.file.size / 1024)} KB</div>
                </div>
              </div>

              <Input
                className="lg:col-span-2 rounded-none text-xs"
                value={row.title}
                onChange={(e) => patch(row.key, { title: e.target.value })}
                placeholder="Title"
                data-testid={`uploader-title-${row.key}`}
              />

              <select
                className="lg:col-span-2 border border-[#E5E7EB] px-2 py-2 text-xs"
                value={row.tag}
                onChange={(e) => patch(row.key, { tag: e.target.value })}
                data-testid={`uploader-tag-${row.key}`}
              >
                {TAG_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>

              <select
                className="lg:col-span-2 border border-[#E5E7EB] px-2 py-2 text-xs"
                value={row.attach}
                onChange={(e) => patch(row.key, { attach: e.target.value, target_id: "" })}
                data-testid={`uploader-attach-${row.key}`}
              >
                {ATTACH_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>

              <div className="lg:col-span-2">
                {row.attach.startsWith("speaker:") && (
                  <select
                    className="w-full border border-[#E5E7EB] px-2 py-2 text-xs"
                    value={row.target_id}
                    onChange={(e) => patch(row.key, { target_id: e.target.value })}
                    data-testid={`uploader-target-${row.key}`}
                  >
                    <option value="">Choose speaker…</option>
                    {speakers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                )}
                {row.attach.startsWith("mentor:") && (
                  <select
                    className="w-full border border-[#E5E7EB] px-2 py-2 text-xs"
                    value={row.target_id}
                    onChange={(e) => patch(row.key, { target_id: e.target.value })}
                    data-testid={`uploader-target-${row.key}`}
                  >
                    <option value="">Choose mentor…</option>
                    {mentors.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                )}
                {row.attach === "resource" && (
                  <select
                    className="w-full border border-[#E5E7EB] px-2 py-2 text-xs"
                    value={row.day_id}
                    onChange={(e) => patch(row.key, { day_id: e.target.value })}
                    data-testid={`uploader-day-${row.key}`}
                  >
                    <option value="">Day (optional)</option>
                    {days.map((d) => <option key={d.id} value={d.id}>Day {d.day_number}</option>)}
                  </select>
                )}
              </div>

              <div className="lg:col-span-1 flex flex-col items-end gap-1 text-xs">
                {row.status === "pending" && <span className="text-[#525252]">Ready</span>}
                {(row.status === "uploading" || row.status === "attaching") && <span className="text-[#2596be]">{row.status}…</span>}
                {row.status === "done" && (
                  <a href={row.url} target="_blank" rel="noreferrer" className="text-[#2596be] inline-flex items-center gap-1" data-testid={`uploader-done-${row.key}`}>
                    <CheckCircle2 size={12} /> Done
                  </a>
                )}
                {row.status === "error" && (
                  <span className="text-[#A23232] inline-flex items-center gap-1" title={row.error} data-testid={`uploader-error-${row.key}`}>
                    <AlertCircle size={12} /> Failed
                  </span>
                )}
                <button onClick={() => removeRow(row.key)} className="text-[#525252] hover:text-[#A23232]" data-testid={`uploader-remove-${row.key}`}>
                  <Trash2 size={12} />
                </button>
              </div>
              {row.error && <div className="lg:col-span-12 text-xs text-[#A23232] pt-1">{row.error}</div>}
            </div>
          ))}
        </div>
      )}

      {/* What goes where */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
        <div className="border border-[#E5E7EB] p-4">
          <div className="label-eyebrow flex items-center gap-2"><Library size={12} /> Resource Library</div>
          <p className="text-xs text-[#525252] mt-2">Files become entries on /resources, optionally pinned to a day.</p>
        </div>
        <div className="border border-[#E5E7EB] p-4">
          <div className="label-eyebrow flex items-center gap-2"><Megaphone size={12} /> Speaker attach</div>
          <p className="text-xs text-[#525252] mt-2">Photo / bio PDF / deck wire directly into the chosen speaker card. Photo URLs render immediately.</p>
        </div>
        <div className="border border-[#E5E7EB] p-4">
          <div className="label-eyebrow flex items-center gap-2"><GraduationCap size={12} /> Mentor attach</div>
          <p className="text-xs text-[#525252] mt-2">Photo + bio PDF wire into the chosen mentor. <em>AI fill bio</em> on the Mentors page can then auto-fill expertise + availability.</p>
        </div>
      </div>
    </div>
  );
}
